import { ReactNode, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Home, CalendarDays, BarChart3, Coffee, User } from 'lucide-react';
import PaymentBar from './PaymentBar';
import PwaBanner from './PwaBanner';
import EmailBanner from './EmailBanner';
import { ToastContainer, toast } from './Toast';
import useSwipeBack from '../hooks/useSwipeBack';
import './Layout.css';

interface LayoutProps {
  children: ReactNode;
  userId?: number;
}

const tabs = [
  { path: '/', label: 'Главная', icon: Home },
  { path: '/tournaments', label: 'Турниры', icon: CalendarDays },
  { path: '/rating', label: 'Рейтинг', icon: BarChart3 },
  { path: '/menu', label: 'Меню', icon: Coffee },
  { path: '/profile', label: 'Профиль', icon: User },
];

function useKeyboardVisible() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;

    const threshold = 150;
    const onResize = () => {
      const diff = window.innerHeight - vv.height;
      setVisible(diff > threshold);
    };

    vv.addEventListener('resize', onResize);
    return () => vv.removeEventListener('resize', onResize);
  }, []);

  return visible;
}

function useNetworkToast() {
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail === 'offline') {
        toast.error('Нет подключения к сети');
      } else if (detail === 'online') {
        toast.success('Соединение восстановлено');
      }
    };
    window.addEventListener('app:network', handler);
    return () => window.removeEventListener('app:network', handler);
  }, []);
}

export default function Layout({ children, userId }: LayoutProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const keyboardVisible = useKeyboardVisible();
  useSwipeBack();
  useNetworkToast();

  return (
    <div className="layout">
      <ToastContainer />
      <main className="main-content">{children}</main>
      {userId && userId > 0 && <PaymentBar userId={userId} />}
      <EmailBanner userId={userId} />
      <PwaBanner />
      <nav className={`bottom-nav ${keyboardVisible ? 'nav-hidden' : ''}`}>
        <div className="expandable-tabs">
          {tabs.map((tab) => {
            const isActive = tab.path === '/'
              ? location.pathname === '/'
              : location.pathname.startsWith(tab.path);
            const Icon = tab.icon;
            return (
              <button
                key={tab.path}
                className={`expandable-tab ${isActive ? 'active' : ''}`}
                onClick={() => navigate(tab.path)}
              >
                <span className="tab-icon">
                  <Icon size={22} strokeWidth={2} />
                </span>
                <span className="tab-label">{tab.label}</span>
              </button>
            );
          })}
        </div>
      </nav>
    </div>
  );
}
